import type { CreditOrAdjustment, Invoice, InvoiceLineItem } from '@aralere/shared-types';

// ──────────────────────────────────────────────────────────────────────────────
// Proportional tax-reversal math as the SPA sees it (04 #60 WS3, s173; 08 Q59). MIRRORS
// api/src/lib/credits.ts (invoiceLineNet / invoiceLineTaxableNet / invoiceTaxableNet) on the WS2a /
// WS2b preview-mirror precedent: the SERVER IS THE AUTHORITY and re-validates every credit note and
// refund; what lives here drives the dialog hints and the pre-submit caps only (02 s97: a cap that
// re-derives the verdict is a convenience, never a second authority). ONE helper, imported by every
// site - the four inline copies this replaced could not all be right once a line was PARTLY taxable.
//
// The unit: an issued line's FROZEN `taxableNet` is the share of its net that entered the taxable
// base (post the invoice discount's pro-rata allocation; an apportioned class line = its amount x
// the ordinary taxable ratio; a non-taxable line = 0). The invoice's effective rate is
// tax.amount / Σ taxableNet, and a credited net dollar on a line reverses tax by that line's SHARE
// (taxableNet / net) - so an apportioned class line consumes less than (1 + rate) of balance per net
// dollar, and a fully taxable line exactly (1 + rate). A pre-WS3 invoice carries no `taxableNet`
// and no class line; `taxable ? net : 0` is exact for it.
//
// 04 #149 commit 2 (02 invariant 3 s207; s209): the REVENUE a note reverses is its LIST amounts
// less the invoice discount's proportional share (f = (ordinary subtotal - discount) / ordinary
// subtotal; a class line's factor is 1), and BOTH legs are CUMULATIVE over the invoice's surviving
// line-crediting notes and anchored on what those booked (option 3c). creditNotePreview below is the
// full mirror of credits.ts#computeCreditNote; the dialogs render its legs. The server re-validates.
// ──────────────────────────────────────────────────────────────────────────────

const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

/** A composed CREDIT line (04 #45, 02g #45 O5): a negative recurring line carrying `creditReason` - credits.ts mirror. */
export function isCreditLine(line: Pick<InvoiceLineItem, 'creditReason'>): boolean {
  return !!line.creditReason;
}

/** The NET amount of a STORED line (post its own line discount) - the credit/refund cap unit. A composed
 *  CREDIT line (04 #45) nets to its NEGATIVE gross, exactly as credits.ts#invoiceLineNet does, so the
 *  ordinary subtotal and the discount factor below see the credited amount; the dialogs then treat it as
 *  un-creditable (remaining net <= 0). */
export function invoiceLineNet(line: InvoiceLineItem): number {
  const gross = round2(line.charge * line.qty);
  if (isCreditLine(line)) return gross;
  return round2(Math.max(0, gross - (line.discount?.amount ?? 0)));
}

/** The share of a stored line's net that entered the taxable base at issue (frozen; fallback exact
 *  for pre-WS3 invoices, which carry no class line). */
export function invoiceLineTaxableNet(line: InvoiceLineItem): number {
  if (typeof line.taxableNet === 'number') return round2(Math.max(0, line.taxableNet));
  return line.taxable !== false ? invoiceLineNet(line) : 0;
}

/** The invoice's stored taxable net (the denominator of the proportional reversal). */
export function invoiceTaxableNet(invoice: Invoice): number {
  return round2(invoice.lineItems.reduce((s, l) => s + invoiceLineTaxableNet(l), 0));
}

/** An ORDINARY revenue line (not a charge-class line, draft reference or frozen stamp) - credits.ts mirror. */
export function isOrdinaryLine(line: InvoiceLineItem): boolean {
  return !line.chargeClassId && !line.chargeClass;
}

/** 04 #160 WS-C M2a: whether the M1a-or-later freeze wrote this invoice (any line carries a numeric
 *  netAfterDiscount) - credits.ts#frozenAtM1a mirror. */
export function frozenAtM1a(invoice: Pick<Invoice, 'lineItems'>): boolean {
  return invoice.lineItems.some((l) => typeof l.netAfterDiscount === 'number' && Number.isFinite(l.netAfterDiscount));
}

/** Σ invoiceLineNet over ordinary lines - the base the invoice-level discount applied to, AS THAT INVOICE'S
 *  ENGINE MEASURED IT (credits.ts#invoiceOrdinarySubtotal mirror, 04 #160 WS-C M2a): from M1a the POSITIVE
 *  ordinary lines only (02i §8 DP-1), before M1a the signed Σ (credit lines included). */
export function invoiceOrdinarySubtotal(invoice: Invoice): number {
  const positiveOnly = frozenAtM1a(invoice);
  return round2(invoice.lineItems.filter((l) => isOrdinaryLine(l) && !(positiveOnly && isCreditLine(l))).reduce((s, l) => s + invoiceLineNet(l), 0));
}

/** Σ the composed credit lines' FIXED tax (≤ 0; 0 before M2a) - credits.ts#invoiceCreditTax mirror. */
export function invoiceCreditTax(invoice: Invoice): number {
  return round2(invoice.lineItems.reduce((sum, l) => sum + (isCreditLine(l) && typeof l.taxAmount === 'number' && Number.isFinite(l.taxAmount) ? Math.min(0, l.taxAmount) : 0), 0));
}

/** The POSITIVE PART of the stored tax: tax.amount less the credit lines' fixed tax, floored at 0 (02i T1.4) -
 *  the effective rate's numerator (credits.ts#invoicePositiveTax mirror). */
export function invoicePositiveTax(invoice: Invoice): number {
  return round2(Math.max(0, (invoice.tax?.amount ?? 0) - invoiceCreditTax(invoice)));
}

/** The stored invoice-level discount, clamped to [0, ordinary subtotal] as the engine capped it at issue. */
export function invoiceDiscountAmount(invoice: Invoice): number {
  const d = invoice.discount?.amount ?? 0;
  return round2(Math.min(Math.max(0, d), invoiceOrdinarySubtotal(invoice)));
}

/** f = (ordinary subtotal - discount) / ordinary subtotal: the share of an ordinary line's LIST net the invoice
 *  actually charged (08 Q65 DERIVE). No ordinary lines -> 1; a 100%-discounted invoice -> 0. */
export function invoiceDiscountFactor(invoice: Invoice): number {
  const os = invoiceOrdinarySubtotal(invoice);
  if (os <= 0) return 1;
  return (os - invoiceDiscountAmount(invoice)) / os;
}

/** A line's discount factor: f for an ordinary line, 1 for a charge-class line (it takes no invoice discount). */
export function invoiceLineDiscountFactor(invoice: Invoice, line: InvoiceLineItem): number {
  return isOrdinaryLine(line) ? invoiceDiscountFactor(invoice) : 1;
}

/** The kinds whose lines[] credit an invoice line's net (credit notes AND early-pay CRNs) - credits.ts mirror. */
export const LINE_CREDITING_KINDS: ReadonlySet<CreditOrAdjustment['kind']> = new Set(['credit-note', 'early-pay-discount']);

/** What one surviving line-crediting note booked (the 3c anchors) - the credits.ts CreditPriorNote shape. */
export interface CreditPriorNote {
  ordinaryList: number;
  listTotal: number;
  discountReversed?: number;
  taxReversed: number;
}
export interface CreditPriors {
  byLine: Record<string, number>;
  priorNotes: CreditPriorNote[];
  /** 04 #160 WS-C M2a (02i T1.6): per lineId, the LIST-unit figure the ISSUED composed credit lines naming
   *  this invoice's line already reversed (credits.ts#composerCreditsByLine mirror) - the per-line cap and
   *  the remaining reads only, never the legs. Absent = none known. */
  composerByLine?: Record<string, number>;
}
export const EMPTY_CREDIT_PRIORS: CreditPriors = { byLine: {}, priorNotes: [] };

/** Build the priors from the invoice's adjustments (the dialog's FRESH invoice-scoped list): the non-void
 *  line-crediting notes that NAME the invoice - mirrors credits.ts#priorsFromAdjustments. */
export function creditPriorsFrom(invoice: Invoice, adjustments: readonly CreditOrAdjustment[], invoices: readonly Invoice[] = []): CreditPriors {
  const ordinary = new Set(invoice.lineItems.filter(isOrdinaryLine).map((l) => l.lineId));
  // 04 #160 WS-C M2a (02i T1.6): the composed credit lines naming this invoice's lines, from the LOADED
  // invoice list (the server reads the issued set itself; a line the list does not show is caught by the
  // server's credit_exceeds_line). A credit's net is in the line's netAfterDiscount unit; ÷ the line's own
  // factor puts it in list units.
  const composerByLine: Record<string, number> = {};
  const byId = new Map(invoice.lineItems.map((l) => [l.lineId, l] as const));
  for (const inv of invoices) {
    if (inv.id === invoice.id || inv.billingType !== 'monthly' || !['issued', 'paid', 'written-off'].includes(inv.state)) continue;
    for (const c of inv.lineItems) {
      if (!c.creditReason || c.creditsInvoiceId !== invoice.id || !c.creditsLineId) continue;
      const line = byId.get(c.creditsLineId);
      if (!line) continue;
      const credited = Math.abs(round2(c.charge * c.qty));
      const net = invoiceLineNet(line);
      const after = typeof line.netAfterDiscount === 'number' && Number.isFinite(line.netAfterDiscount) ? round2(line.netAfterDiscount) : round2(net * invoiceLineDiscountFactor(invoice, line));
      composerByLine[line.lineId] = round2((composerByLine[line.lineId] ?? 0) + (after > 0 ? round2((credited * net) / after) : credited));
    }
  }
  const byLine: Record<string, number> = {};
  const priorNotes: CreditPriorNote[] = [];
  for (const a of adjustments) {
    if (!LINE_CREDITING_KINDS.has(a.kind) || a.status === 'voided' || a.invoiceId !== invoice.id) continue;
    let ordinaryList = 0;
    let listTotal = 0;
    for (const ln of a.lines ?? []) {
      byLine[ln.lineId] = round2((byLine[ln.lineId] ?? 0) + ln.amount);
      listTotal = round2(listTotal + ln.amount);
      if (ordinary.has(ln.lineId)) ordinaryList = round2(ordinaryList + ln.amount);
    }
    priorNotes.push({
      ordinaryList,
      listTotal,
      ...(typeof a.discountReversed === 'number' ? { discountReversed: round2(a.discountReversed) } : {}),
      taxReversed: round2(a.taxReversed ?? 0),
    });
  }
  return Object.keys(composerByLine).length > 0 ? { byLine, priorNotes, composerByLine } : { byLine, priorNotes };
}

/** A prior note's discount leg: stored, else derived as if stored (a pre-R2 note) - credits.ts mirror. */
function priorNoteDiscountReversed(invoice: Invoice, note: CreditPriorNote): number {
  if (typeof note.discountReversed === 'number') return round2(note.discountReversed);
  const os = invoiceOrdinarySubtotal(invoice);
  return os > 0 ? round2((invoiceDiscountAmount(invoice) * note.ordinaryList) / os) : 0;
}

export interface CreditNotePreview {
  listTotal: number;
  discountReversed: number;
  creditedSubtotal: number;
  taxReversed: number;
  total: number;
}

/** The FULL mirror of credits.ts#computeCreditNote for a set of LIST amounts by lineId (unknown lineIds are
 *  ignored; no caps are judged here - the server does that): the discount leg and the tax leg, each the
 *  cumulative entitlement over priors + these lines rounded once, less what the priors booked. */
export function creditNotePreview(invoice: Invoice, credited: readonly { lineId: string; amount: number }[], priors: CreditPriors = EMPTY_CREDIT_PRIORS): CreditNotePreview {
  const byId = new Map(invoice.lineItems.map((l) => [l.lineId, l] as const));
  const thisByLine: Record<string, number> = {};
  let listTotal = 0;
  let thisOrdinaryList = 0;
  for (const c of credited) {
    const line = byId.get(c.lineId);
    const amount = round2(Math.max(0, c.amount || 0));
    if (!line || amount <= 0) continue;
    thisByLine[c.lineId] = round2((thisByLine[c.lineId] ?? 0) + amount);
    listTotal = round2(listTotal + amount);
    if (isOrdinaryLine(line)) thisOrdinaryList = round2(thisOrdinaryList + amount);
  }
  // No note without lines (the server refuses an empty note): an empty selection previews as zeros rather than as
  // the survivors' one-cent lag behind the cumulative entitlement, which only the NEXT real note absorbs (3c;
  // observed s209 on 0029 after the D5 void: -0.01 / +0.01 with nothing selected).
  if (listTotal <= 0) return { listTotal: 0, discountReversed: 0, creditedSubtotal: 0, taxReversed: 0, total: 0 };
  const os = invoiceOrdinarySubtotal(invoice);
  const discount = invoiceDiscountAmount(invoice);
  let priorOrdinaryList = 0;
  let priorDiscountReversed = 0;
  let priorTaxReversed = 0;
  for (const n of priors.priorNotes) {
    priorOrdinaryList = round2(priorOrdinaryList + n.ordinaryList);
    priorDiscountReversed = round2(priorDiscountReversed + priorNoteDiscountReversed(invoice, n));
    priorTaxReversed = round2(priorTaxReversed + n.taxReversed);
  }
  const cumulativeDiscountReversed = os > 0 && discount > 0 ? round2((discount * round2(priorOrdinaryList + thisOrdinaryList)) / os) : 0;
  const discountReversed = round2(cumulativeDiscountReversed - priorDiscountReversed);
  const creditedSubtotal = round2(listTotal - discountReversed);
  let cumulativeTaxableNetExact = 0;
  for (const line of invoice.lineItems) {
    const net = invoiceLineNet(line);
    const taxableNet = invoiceLineTaxableNet(line);
    const cr = (priors.byLine[line.lineId] ?? 0) + (thisByLine[line.lineId] ?? 0);
    if (cr > 0 && taxableNet > 0 && net > 0) cumulativeTaxableNetExact += (cr * taxableNet) / net;
  }
  // 04 #160 WS-C M2a (02i T1.4): the POSITIVE part of the stored tax is the effective rate's numerator.
  const storedTax = invoicePositiveTax(invoice);
  const storedTaxableNet = invoiceTaxableNet(invoice);
  const cumulativeTax = storedTax > 0 && storedTaxableNet > 0 ? round2((storedTax * round2(cumulativeTaxableNetExact)) / storedTaxableNet) : 0;
  const taxReversed = round2(cumulativeTax - priorTaxReversed);
  return { listTotal, discountReversed, creditedSubtotal, taxReversed, total: round2(creditedSubtotal + taxReversed) };
}

/** The invoice's EFFECTIVE tax rate: stored tax over the stored taxable net (an invoice discount
 *  lowers it below the nominal rate; a rate-only mirror overstated every per-line preview, s78). */
export function invoiceEffectiveTaxRate(invoice: Invoice): number {
  const storedTax = invoicePositiveTax(invoice); // 04 #160 WS-C M2a: the positive part (02i T1.4)
  const storedTaxableNet = invoiceTaxableNet(invoice);
  return storedTax > 0 && storedTaxableNet > 0 ? storedTax / storedTaxableNet : 0;
}

/** The fraction (0..1) of a line's net that is taxable - 1 for a fully taxable line, 0 for a
 *  non-taxable one, in between for an apportioned charge-class line. */
export function invoiceLineTaxShare(line: InvoiceLineItem): number {
  const net = invoiceLineNet(line);
  if (net <= 0) return 0;
  return Math.min(1, Math.max(0, invoiceLineTaxableNet(line) / net));
}

/** PRE-R2 per-dollar factor: 1 + effectiveRate x taxShare (the whole list dollar as revenue plus its tax).
 *  Kept for the harness's attribution checks; the dialogs use invoiceLineBalanceFactor below (04 #149). */
export function invoiceLineTaxFactor(invoice: Invoice, taxShare: number): number {
  if (!invoice.tax?.enabled) return 1;
  return 1 + invoiceEffectiveTaxRate(invoice) * Math.min(1, Math.max(0, taxShare));
}

/** How much BALANCE one credited/refunded LIST dollar on this line consumes under R2: its discount factor
 *  (f for an ordinary line, 1 for a class line) plus the tax it carries (effectiveRate x taxShare). The linear
 *  per-dollar figure the dialog caps use; the exact note (cumulative, rounded once) is creditNotePreview. */
export function invoiceLineBalanceFactor(invoice: Invoice, line: InvoiceLineItem): number {
  const share = invoiceLineTaxShare(line);
  const tax = invoice.tax?.enabled ? invoiceEffectiveTaxRate(invoice) * share : 0;
  return invoiceLineDiscountFactor(invoice, line) + tax;
}

/** A reversed LIST amount's GROSS relief (revenue + its tax) on a line: list x its balance factor. */
export function invoiceLineGross(invoice: Invoice, net: number, line: InvoiceLineItem): number;
/** @deprecated pre-R2 form (treats the whole list dollar as revenue); kept for the harness's attribution checks. */
export function invoiceLineGross(invoice: Invoice, net: number, taxShare: number): number;
export function invoiceLineGross(invoice: Invoice, net: number, lineOrShare: InvoiceLineItem | number): number {
  if (typeof lineOrShare === 'number') return round2(net * invoiceLineTaxFactor(invoice, lineOrShare));
  return round2(net * invoiceLineBalanceFactor(invoice, lineOrShare));
}

/** The tax a set of credited lines reverses WITH NO PRIOR NOTES (the per-note method (i) form, s207 commit 1):
 *  storedTax x round2(Σ amount x share) / storedTaxableNet. Equal to creditNotePreview(...).taxReversed with
 *  empty priors; the dialogs use creditNotePreview so the 3c anchoring is mirrored too. */
export function creditTaxReversed(invoice: Invoice, credited: readonly { amount: number; taxShare: number }[]): number {
  const storedTax = invoice.tax?.amount ?? 0;
  const storedTaxableNet = invoiceTaxableNet(invoice);
  if (!(storedTax > 0) || !(storedTaxableNet > 0)) return 0;
  const creditedTaxableNet = round2(credited.reduce((s, c) => s + c.amount * Math.min(1, Math.max(0, c.taxShare)), 0));
  return round2((storedTax * creditedTaxableNet) / storedTaxableNet);
}
